import { useCallback, useMemo, useState } from 'react';

import { AlertCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FieldTypes } from 'constants/field-types-enum';
import { meetsLogic } from 'constants/logic';
import { componentMap, getComponentProps, handleLogicJump } from 'helpers/question-config';
import { useKeyboardHints } from 'hooks/keyboard-hints';

import FormWrapper from '../Form/FormWrapper';

// Normalize answer value so meetsLogic gets a consistent shape (e.g. yes_no as "Yes"/"No").
const normalizeAnswerForRule = (question, value) => {
  if (value === null || value === undefined || value === '') return value;
  if (question?.type === FieldTypes.YesNo) {
    if (value === true || value === 'true' || value === 'Yes') return 'Yes';
    if (value === false || value === 'false' || value === 'No') return 'No';
  }
  return value;
};

// Check if a candidate's answer triggers a block rule for the current question.
// Returns the blocking rule if one matches, otherwise null.
// Rule.field from the API is the question's order (1-based). Match only by order to avoid false
// positives when question.id happens to equal another question's order.
const getBlockingRule = (rules, question, answerValue, allQuestions = []) => {
  if (!rules || !rules.length || !question) return null;
  const rawOrder = question.order;
  const questionOrder =
    rawOrder != null && rawOrder !== '' && !Number.isNaN(Number(rawOrder))
      ? Number(rawOrder)
      : null;
  const idx =
    questionOrder == null && allQuestions.length > 0
      ? allQuestions.findIndex(
          q => q && (q.id === question.id || q.id?.toString() === question.id?.toString()),
        )
      : -1;
  const orderFromIndex = idx >= 0 ? idx + 1 : null;
  const effectiveOrder = questionOrder ?? orderFromIndex;
  if (effectiveOrder == null) return null;

  const blockRules = rules.filter(r => {
    if (r.rule_type !== 'block') return false;
    if (!r.active) return false;
    const ruleField = Number(r.field);
    return ruleField === effectiveOrder;
  });
  const normalizedValue = normalizeAnswerForRule(question, answerValue);
  return blockRules.find(rule => meetsLogic(rule, normalizedValue)) || null;
};

const RefereeFormWrapper = ({
  formMessage,
  questions,
  logicJumps,
  rules = [],
  textParams = {},
  brand,
  candidateId,
  getCandidateUpload,
  signCandidateUpload,
  createCandidateUpload,
  deleteCandidateUpload,
  onSubmit = () => {},
  locale = 'en_GB',
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [formCompleted, setFormCompleted] = useState(false);
  const [backward, setBackward] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState(new Set());
  const [blockMessage, setBlockMessage] = useState(null);

  const handlers = {
    getCandidateUpload,
    signCandidateUpload,
    createCandidateUpload,
    deleteCandidateUpload,
  };

  const filteredQuestions = useMemo(() => {
    return questions.filter(q => !skippedQuestions.has(q.order));
  }, [questions, skippedQuestions]);

  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const keyboardHint = useKeyboardHints(currentQuestion?.type);

  const handleNext = useCallback(
    async formValues => {
      setBackward(false);

      // Check block rules before advancing
      const currentQuestionValue = formValues[currentQuestion.id.toString()];
      const blockRule = getBlockingRule(rules, currentQuestion, currentQuestionValue, questions);
      if (blockRule) {
        setBlockMessage(blockRule.message || 'You cannot proceed based on your answer.');
        return;
      }

      // Clear any previous block message
      setBlockMessage(null);

      if (currentQuestionIndex < filteredQuestions.length - 1) {
        const { jumped, newSkipped, jumpToIndex } = handleLogicJump(
          formValues,
          logicJumps,
          currentQuestion,
          currentQuestionValue,
          skippedQuestions,
          filteredQuestions,
        );

        const updatedFiltered = questions.filter(q => !newSkipped.has(q.order));

        setSkippedQuestions(newSkipped);

        if (jumped) {
          if (Number(jumpToIndex) === -1) {
            // "Finish form" jump: no question has order -1; complete the form immediately
            setFormCompleted(true);
            const filteredFormValues = Object.fromEntries(
              Object.entries(formValues).filter(([key]) =>
                updatedFiltered.some(q => q.id.toString() === key),
              ),
            );
            onSubmit(filteredFormValues);
          } else {
            const nextIndex = updatedFiltered.findIndex(q => q.order === jumpToIndex);
            setCurrentQuestionIndex(nextIndex !== -1 ? nextIndex : currentQuestionIndex + 1);
          }
        } else {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
      } else {
        setFormCompleted(true);
        const filteredFormValues = Object.fromEntries(
          Object.entries(formValues).filter(([key]) =>
            filteredQuestions.some(q => q.id.toString() === key),
          ),
        );
        onSubmit(filteredFormValues);
      }
    },
    [
      currentQuestionIndex,
      filteredQuestions,
      currentQuestion,
      logicJumps,
      onSubmit,
      rules,
      skippedQuestions,
      questions,
    ],
  );

  const handleBack = useCallback(() => {
    setBackward(true);
    setBlockMessage(null);
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(currentQuestionIndex - 1);
  }, [currentQuestionIndex]);

  const getQuestionProps = value =>
    getComponentProps(currentQuestion, value, candidateId, handlers, brand);

  return (
    <div className="relative">
      <FormWrapper
        question={currentQuestion}
        questionIndex={currentQuestionIndex}
        totalQuestions={filteredQuestions.length}
        blockMessage={blockMessage}
        formMessage={formMessage}
        textParams={textParams}
        formCompleted={formCompleted}
        locale={locale}
        backward={backward}
        brand={brand}
        componentMap={componentMap}
        keyboardHint={keyboardHint}
        getComponentProps={getQuestionProps}
        onNext={handleNext}
        onBack={handleBack}
      />
    </div>
  );
};

export default RefereeFormWrapper;
